import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import Icon from '../Icon';

const problems = [
  {
    icon: 'groups',
    title: 'Fairness Under Fire',
    description: 'When 1,000 people bid in the same second, who wins? Traditional databases can\'t guarantee order.',
    color: 'red',
  },
  {
    icon: 'bolt',
    title: 'Race Conditions',
    description: 'Two bids arrive simultaneously. Both read "current bid: £50". Both try to set £55. One overwrites the other.',
    color: 'orange',
  },
  {
    icon: 'timer',
    title: 'Latency Kills',
    description: 'A 200ms round-trip to a central database means your bid might be stale before it arrives.',
    color: 'amber',
  },
  {
    icon: 'shuffle',
    title: 'Ordering Nightmares',
    description: 'Distributed systems reorder messages. Without guarantees, "first" bid might arrive last.',
    color: 'purple',
  },
];

const failedApproaches = [
  { name: 'Redis', issue: 'Fast, but no built-in ordering or atomic bid logic' },
  { name: 'Postgres Locks', issue: 'Correct, but serialized = slow under load' },
  { name: 'Optimistic Locking', issue: 'Retries explode during bid wars' },
  { name: 'Message Queues', issue: 'Async delays break real-time UX' },
];

export default function ProblemSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const colorClasses: Record<string, { bg: string; icon: string; border: string }> = {
    red: { bg: 'bg-red-50', icon: 'text-red-500', border: 'border-red-200' },
    orange: { bg: 'bg-orange-50', icon: 'text-orange-500', border: 'border-orange-200' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-500', border: 'border-amber-200' },
    purple: { bg: 'bg-purple-50', icon: 'text-purple-500', border: 'border-purple-200' },
  };

  return (
    <section ref={ref} className="py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 bg-red-100 text-red-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
            <Icon name="warning" size="small" />
            The Hard Problem
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Why Auctions Are Deceptively Difficult
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Real-time bidding seems simple. Accept bids, track the highest. 
            But at scale, everything breaks.
          </p>
        </motion.div>

        {/* Problem Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          {problems.map((problem, i) => {
            const colors = colorClasses[problem.color];
            return (
              <motion.div
                key={problem.title}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.1 }}
                className={`${colors.bg} ${colors.border} border rounded-xl p-5`}
              >
                <div className="flex items-start gap-4">
                  <div className={`${colors.icon} mt-1`}>
                    <Icon name={problem.icon} className="text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{problem.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{problem.description}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Failed Approaches */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="bg-gray-900 rounded-2xl p-6 text-white"
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Icon name="block" className="text-red-400" />
            Why Naive Solutions Fail
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {failedApproaches.map((approach, i) => (
              <motion.div
                key={approach.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.3, delay: 0.7 + i * 0.1 }}
                className="bg-gray-800 rounded-lg p-3 text-center"
              >
                <div className="text-sm font-medium text-gray-300 mb-1">{approach.name}</div>
                <div className="text-xs text-gray-500">{approach.issue}</div>
              </motion.div>
            ))}
          </div>
          <p className="text-sm text-gray-400 mt-4 text-center">
            We needed a different approach. One that's <strong className="text-white">fast</strong>, <strong className="text-white">fair</strong>, and <strong className="text-white">correct by design</strong>.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
